import React, { useEffect, useState } from 'react';
import { Blog } from './BlogData';
import { CiCalendar } from 'react-icons/ci';
import { FiClock } from 'react-icons/fi';
import { FaInstagram, FaFilePdf, FaBookOpen, FaTimes, FaUsers, FaBuilding, FaAward, FaCalendarAlt, FaExternalLinkAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';

interface BlogListProps {
  blogs?: Blog[];
  limit?: number;
}

interface NewsStructure {
  id: string;
  title: string;
  img: string;
  desc?: string;
  content?: string;
  date: string;
  instagram_reel?: string;
  RMBiansOfMeeting?: any;
  gallery?: any;
  mom_pdf?: string;
  presentation_1?: string;
  presentation_2?: string;
  location?: string;
  venue?: string;
  chapter?: string;
  event_type?: string;
}

const formatCleanText = (rawStr?: string): string => {
  if (!rawStr) return '';
  // 1. Strip HTML tags
  let clean = rawStr.replace(/<[^>]*>/g, '');
  // 2. Decode HTML entities
  clean = clean
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&hellip;/gi, '...')
    .replace(/&ndash;/gi, '-')
    .replace(/&mdash;/gi, '--');
  // 3. Normalize whitespace
  return clean.replace(/\s+/g, ' ').trim();
};

const BlogList: React.FC<BlogListProps> = ({ limit }) => {
  const [newData, setNewData] = useState<NewsStructure[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedHighlight, setSelectedHighlight] = useState<NewsStructure | null>(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number>(0);

  useEffect(() => {
    axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      .then(res => setAllUsers(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error("Error fetching users in BlogList:", err));

    const fetchData = async () => {
      try {
        const [newsRes, eventsRes] = await Promise.all([
          axios.get<NewsStructure[]>("https://rmbbackend.rmbjaipur.com/auth/news").catch(() => ({ data: [] })),
          axios.get<any[]>("https://rmbbackend.rmbjaipur.com/auth/events").catch(() => ({ data: [] }))
        ]);

        const newsList = Array.isArray(newsRes.data) ? newsRes.data : [];
        const eventsList = Array.isArray(eventsRes.data) ? eventsRes.data : [];

        const now = new Date();
        now.setHours(0, 0, 0, 0); // Start of today

        // Filter events where date is LESS THAN current date (Past Events & Completed General Meetings)
        const pastEvents = eventsList.filter((e: any) => {
          const eDateStr = e.event_date || e.start_date;
          if (!eDateStr) return false;
          const eDate = new Date(eDateStr);
          return eDate < now;
        }).map((e: any) => {
          let photo = 'uploads/default_news.jpg';
          let galleryPhotos: any = [];
          const rawPhotos = e.photographs || e.photos || e.gallery || e.img || e.image;
          if (rawPhotos) {
            try {
              const parsed = typeof rawPhotos === 'string' ? JSON.parse(rawPhotos) : rawPhotos;
              if (Array.isArray(parsed) && parsed.length > 0) {
                photo = parsed[0];
                galleryPhotos = parsed;
              } else if (typeof rawPhotos === 'string' && rawPhotos.includes('&')) {
                galleryPhotos = rawPhotos.split('&').filter(Boolean);
                photo = galleryPhotos[0] || rawPhotos;
              } else if (typeof rawPhotos === 'string') {
                photo = rawPhotos;
                galleryPhotos = [rawPhotos];
              }
            } catch {
              if (typeof rawPhotos === 'string' && rawPhotos.includes('&')) {
                galleryPhotos = rawPhotos.split('&').filter(Boolean);
                photo = galleryPhotos[0] || rawPhotos;
              } else {
                photo = rawPhotos;
                galleryPhotos = [rawPhotos];
              }
            }
          }

          const evtDesc = e.description || e.desc || e.content || e.heading || '';
          const defaultContent = `General Meeting / Chapter Event conducted at ${e.location || e.venue || 'Jaipur'}.${e.presentation_1 ? `\n\nPresenter 1: ${e.presentation_1}` : ''}${e.presentation_2 ? `\nPresenter 2: ${e.presentation_2}` : ''}`;

          return {
            id: `event-${e.id}`,
            title: e.heading || e.title || evtDesc || e.location || `Past Chapter Event #${e.id}`,
            img: photo,
            gallery: galleryPhotos,
            content: evtDesc || defaultContent,
            desc: evtDesc || defaultContent,
            description: evtDesc || defaultContent,
            date: e.event_date || e.start_date,
            instagram_reel: e.instagram_reel,
            mom_pdf: e.mom_pdf,
            presentation_1: e.presentation_1,
            presentation_2: e.presentation_2,
            location: e.location || e.venue,
            event_type: e.event_type || 'Meeting',
            RMBiansOfMeeting: e.rmbian_of_meeting
          };
        });

        const combined = [...newsList, ...pastEvents];
        combined.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());

        setNewData(combined);
      } catch (error) {
        console.error("News/Events fetch error:", error);
      }
    };

    fetchData();
  }, []);

  const displayedItems = limit ? newData.slice(0, limit) : newData;

  const extractAllPhotos = (item: NewsStructure): string[] => {
    const photos: string[] = [];
    if (item.img) photos.push(item.img);

    if (item.gallery) {
      if (typeof item.gallery === 'string') {
        const split = item.gallery.split('&').filter(Boolean);
        split.forEach(p => { if (!photos.includes(p)) photos.push(p); });
      } else if (Array.isArray(item.gallery)) {
        item.gallery.forEach((p: string) => { if (p && !photos.includes(p)) photos.push(p); });
      }
    }

    return photos.map(p => p.startsWith('http') ? p : `https://rmbbackend.rmbjaipur.com/${p.replace(/\\/g, '/')}`);
  };

  const currentPhotos = selectedHighlight ? extractAllPhotos(selectedHighlight) : [];

  return (
    <div className='bg-tabbg py-8'>
      <div className="flex flex-wrap gap-y-6 w-[100%] justify-between mt-6">
        {displayedItems.length === 0 ? (
          <div className="text-slate-400 font-medium py-10 text-center w-full">
            No past events or news highlights found.
          </div>
        ) : (
          displayedItems.map((data) => (
            <div key={data.id} className="bg-[#344056] border z-30 border-[#64748B] hover:border-[#FDBC12] rounded-2xl p-5 relative md:w-[32%] w-[100%] flex flex-col justify-between shadow-xl transition">
              <div>
                <span className="bg-[#FDBC12] text-[#101729] text-xs font-bold rounded-tr-2xl rounded-bl-lg absolute top-[20px] right-[20px] py-1.5 px-3 shadow">
                  {data.id.toString().startsWith('event-') ? '📅 Past Event' : '📰 News Highlight'}
                </span>

                <div className="w-full h-[220px] rounded-t-2xl overflow-hidden mb-4 bg-slate-900 flex items-center justify-center cursor-pointer border-b border-slate-700/50" onClick={() => { setSelectedHighlight(data); setActivePhotoIndex(0); }}>
                  <img
                    className="w-full h-full object-contain max-h-[220px] mx-auto hover:scale-105 transition duration-500"
                    src={data.img ? (data.img.startsWith('http') ? data.img : `https://rmbbackend.rmbjaipur.com/${data.img.replace(/\\/g, '/')}`) : 'https://rmbbackend.rmbjaipur.com/uploads/default_news.jpg'}
                    alt={data.title}
                    onError={(e) => { e.currentTarget.src = 'https://rmbbackend.rmbjaipur.com/uploads/default_news.jpg'; }}
                  />
                </div>

                <div className="flex flex-wrap justify-between my-3 w-[100%] text-xs text-[#94A2B8]">
                  <div className="flex items-center gap-1">
                    <CiCalendar className="text-[#FDBC12]" />
                    <span>{data.date ? new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Past Event'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FiClock className="text-[#94A2B8]" />
                    <span>3 Min Read</span>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-[#F5F5F5] line-clamp-2 mb-2 leading-snug cursor-pointer hover:text-[#FDBC12] transition" onClick={() => { setSelectedHighlight(data); setActivePhotoIndex(0); }}>
                  {formatCleanText(data.title)}
                </h3>
                
                <p className="text-xs text-slate-300 line-clamp-3 mb-4 leading-relaxed">
                  {formatCleanText(data.content || data.desc || (data as any).description || 'Chapter event and meeting proceedings.')}
                </p>
              </div>

              <div className="space-y-2 mt-4">
                <a
                  href={data.instagram_reel || 'https://www.instagram.com/rotarymeansbusinessjaipur'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-amber-500 hover:from-purple-700 hover:via-pink-600 hover:to-amber-600 text-white font-extrabold text-xs py-2.5 px-3 rounded-lg shadow-md flex items-center justify-center gap-2 transition cursor-pointer"
                >
                  <FaInstagram className="text-base text-white animate-bounce" /> Watch Reel on Instagram
                </a>

                <button
                  onClick={() => { setSelectedHighlight(data); setActivePhotoIndex(0); }}
                  className="w-[100%] bg-[#465468] py-2.5 border border-[#64748B] rounded-lg text-[#D7D3D2] text-sm font-bold hover:bg-[#FDBC12] hover:text-[#101729] hover:border-[#FDBC12] transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FaBookOpen /> Read Highlights →
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* FULL UNCROPPED EVENT HIGHLIGHTS & PHOTO GALLERY MODAL */}
      {selectedHighlight && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#1E2B45] border border-[#2A3B5C] rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6 text-slate-100 shadow-2xl space-y-4">
            
            {/* Header */}
            <div className="flex justify-between items-start border-b border-[#2A3B5C] pb-3">
              <div>
                <span className="bg-[#FDBC12] text-[#0A2451] text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full">
                  {selectedHighlight.id.toString().startsWith('event-') ? '📅 Past Event Highlights' : '📰 News & Meeting Summary'}
                </span>
                <h2 className="text-xl font-black text-white mt-1 leading-snug">{formatCleanText(selectedHighlight.title)}</h2>
                <p className="text-xs text-slate-400 flex items-center gap-2 mt-1 font-semibold">
                  <FaCalendarAlt className="text-[#FDBC12]" /> 
                  {selectedHighlight.date ? new Date(selectedHighlight.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Chapter Event'}
                </p>
              </div>
              <button 
                onClick={() => setSelectedHighlight(null)}
                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 font-bold transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* UNCROPPED FULL PHOTO DISPLAY */}
            <div className="w-full bg-[#0A111E] rounded-2xl border border-[#2A3B5C] p-3 relative flex items-center justify-center min-h-[300px] max-h-[460px] overflow-hidden group shadow-2xl">
              <img
                src={currentPhotos[activePhotoIndex] || 'https://via.placeholder.com/600x400?text=RMB+Event'}
                alt={selectedHighlight.title}
                className="max-h-[440px] w-auto max-w-full object-contain mx-auto transition-all duration-300 shadow-2xl rounded-xl"
              />

              {currentPhotos.length > 1 && (
                <>
                  <button
                    onClick={() => setActivePhotoIndex((prev) => (prev === 0 ? currentPhotos.length - 1 : prev - 1))}
                    className="absolute left-3 w-10 h-10 rounded-full bg-black/70 hover:bg-[#FDBC12] text-white hover:text-[#0A2451] flex items-center justify-center font-bold text-xl border border-white/20 transition shadow-2xl cursor-pointer opacity-80 hover:opacity-100"
                  >
                    <FaChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setActivePhotoIndex((prev) => (prev === currentPhotos.length - 1 ? 0 : prev + 1))}
                    className="absolute right-3 w-10 h-10 rounded-full bg-black/70 hover:bg-[#FDBC12] text-white hover:text-[#0A2451] flex items-center justify-center font-bold text-xl border border-white/20 transition shadow-2xl cursor-pointer opacity-80 hover:opacity-100"
                  >
                    <FaChevronRight size={16} />
                  </button>

                  <div className="absolute bottom-4 right-4 bg-black/80 text-[#FDBC12] text-xs font-black px-3 py-1 rounded-full border border-[#FDBC12]/40 shadow-lg">
                    {activePhotoIndex + 1} / {currentPhotos.length}
                  </div>
                </>
              )}
            </div>

            {/* RELATED PHOTOGRAPHS THUMBNAIL RIBBON */}
            {currentPhotos.length > 1 && (
              <div className="space-y-1.5 bg-[#131E32] p-3 rounded-xl border border-[#2A3B5C]">
                <span className="text-[11px] font-bold text-[#FDBC12] uppercase tracking-wider block">
                  📷 Related Event Photographs ({currentPhotos.length} Photos Added by Admin):
                </span>
                <div className="flex gap-2.5 overflow-x-auto py-1 px-0.5 no-scrollbar scroll-smooth">
                  {currentPhotos.map((photoUrl, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActivePhotoIndex(idx)}
                      className={`w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${
                        activePhotoIndex === idx
                          ? 'border-[#FDBC12] scale-105 shadow-xl ring-2 ring-[#FDBC12]/50'
                          : 'border-[#2A3B5C] opacity-60 hover:opacity-100 hover:border-slate-300'
                      }`}
                    >
                      <img src={photoUrl} alt={`Event photo ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* RMBian of the Meeting Badge */}
            {selectedHighlight.RMBiansOfMeeting && (() => {
              const rmbian = allUsers.find((u: any) => String(u.id) === String(selectedHighlight.RMBiansOfMeeting));
              if (!rmbian) return null;
              return (
                <div className="bg-gradient-to-r from-[#0A2451] to-[#131E32] p-3.5 rounded-xl border border-[#FDBC12]/60 flex items-center justify-between shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#FDBC12] p-0.5 bg-slate-900 flex-shrink-0 shadow-inner">
                      <img
                        src={rmbian.profile ? (rmbian.profile.startsWith('http') ? rmbian.profile : `https://rmbbackend.rmbjaipur.com/${rmbian.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/60?text=RMB'}
                        alt={rmbian.name}
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/60?text=RMB'; }}
                      />
                    </div>
                    <div>
                      <span className="text-[10px] font-black text-[#FDBC12] uppercase tracking-wider block">🏆 RMBian of the Meeting</span>
                      <h4 className="text-sm font-black text-white">{rmbian.name}</h4>
                      <p className="text-[11px] text-slate-300 font-semibold">{rmbian.company_name || rmbian.designation || 'RMB Jaipur Member'}</p>
                    </div>
                  </div>
                  <span className="bg-[#FDBC12] text-[#0A2451] text-xs font-black px-3 py-1 rounded-full shadow">
                    Honored Member
                  </span>
                </div>
              );
            })()}

            {/* Location & Chapter Badges */}
            {(selectedHighlight.location || selectedHighlight.venue || selectedHighlight.chapter) && (
              <div className="flex flex-wrap gap-2 text-xs font-semibold">
                {(selectedHighlight.location || selectedHighlight.venue) && (
                  <span className="bg-[#131E32] text-rose-400 border border-rose-500/30 px-3 py-1 rounded-lg flex items-center gap-1.5">
                    📍 Venue: {selectedHighlight.location || selectedHighlight.venue}
                  </span>
                )}
                {selectedHighlight.chapter && (
                  <span className="bg-[#131E32] text-amber-300 border border-amber-500/30 px-3 py-1 rounded-lg flex items-center gap-1.5">
                    🏛️ Chapter: {selectedHighlight.chapter}
                  </span>
                )}
              </div>
            )}

            {/* Content Details */}
            <div className="bg-[#131E32] p-4 rounded-xl border border-[#2A3B5C] space-y-3 text-xs">
              <h4 className="font-bold text-[#FDBC12] uppercase text-[11px] tracking-wider">📝 Event Summary & Minutes of Meeting</h4>
              <p className="text-slate-200 leading-relaxed font-medium whitespace-pre-line text-xs">
                {formatCleanText(selectedHighlight.content || selectedHighlight.desc || (selectedHighlight as any).description || 'Full event proceedings and networking summary.')}
              </p>
            </div>

            {/* Dynamic Event Type Presenters & Speakers info */}
            {selectedHighlight.presentation_1 && selectedHighlight.event_type !== 'Social' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="bg-[#131E32] p-3 rounded-xl border border-emerald-500/30">
                  <span className="text-emerald-400 font-bold block mb-1">
                    {selectedHighlight.event_type === 'Installation'
                      ? '👑 Chief Guest Name:'
                      : selectedHighlight.event_type === 'Webinar'
                      ? '🎙️ Keynote Speaker:'
                      : selectedHighlight.event_type === 'Field Visit'
                      ? '📍 Field Visit Host Member:'
                      : '💼 Presentation 1 (Speaker):'}
                  </span>
                  <p className="text-white font-semibold">{selectedHighlight.presentation_1}</p>
                </div>
                {selectedHighlight.presentation_2 && (
                  <div className="bg-[#131E32] p-3 rounded-xl border border-purple-500/30">
                    <span className="text-purple-400 font-bold block mb-1">
                      {selectedHighlight.event_type === 'Installation'
                        ? '🏢 Chief Guest Organization:'
                        : selectedHighlight.event_type === 'Webinar'
                        ? '🏢 Speaker Company / Role:'
                        : selectedHighlight.event_type === 'Field Visit'
                        ? '🏢 Business Name:'
                        : '🎓 Presentation 2 / Education:'}
                    </span>
                    <p className="text-white font-semibold">{selectedHighlight.presentation_2}</p>
                  </div>
                )}
              </div>
            )}

            {/* EMBEDDED MINUTES OF MEETING (MoM) PDF VIEWER */}
            {selectedHighlight.mom_pdf && (
              <div className="bg-[#131E32] p-4 rounded-xl border border-[#2A3B5C] space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#FDBC12] uppercase tracking-wider flex items-center gap-1.5">
                    <FaFilePdf className="text-red-400 text-sm animate-pulse" /> Official Minutes of Meeting (MoM) PDF Document:
                  </span>
                  <a
                    href={`https://rmbbackend.rmbjaipur.com/${selectedHighlight.mom_pdf.replace(/\\/g, '/')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-bold text-amber-300 hover:text-white flex items-center gap-1 underline"
                  >
                    Open Full Screen <FaExternalLinkAlt size={10} />
                  </a>
                </div>

                <div className="w-full h-[400px] rounded-xl overflow-hidden border border-[#2A3B5C] bg-slate-900 shadow-inner">
                  <iframe
                    src={`https://rmbbackend.rmbjaipur.com/${selectedHighlight.mom_pdf.replace(/\\/g, '/')}`}
                    title="Minutes of Meeting PDF Document"
                    className="w-full h-full border-0"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2A3B5C] pt-4">
              {selectedHighlight.mom_pdf ? (
                <a
                  href={`https://rmbbackend.rmbjaipur.com/${selectedHighlight.mom_pdf.replace(/\\/g, '/')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-[#02256D] hover:bg-blue-900 text-white font-bold text-xs rounded-xl shadow-lg border border-[#FDBC12]/40 flex items-center gap-2 transition"
                >
                  <FaFilePdf className="text-red-400 text-sm animate-pulse" /> Download MoM PDF Document
                </a>
              ) : <div />}

              <a
                href={selectedHighlight.instagram_reel || 'https://www.instagram.com/rotarymeansbusinessjaipur'}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2.5 bg-gradient-to-r from-purple-600 via-pink-500 to-amber-500 hover:from-purple-700 text-white font-extrabold text-xs rounded-xl shadow-lg flex items-center gap-2 transition"
              >
                <FaInstagram /> Watch Video Reel <FaExternalLinkAlt size={10} />
              </a>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default BlogList;
